import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { getProductsWithMaxUserByPartnerId } from '../../dave-data-module/State/selectors/products.selectors';
import { UsersActionTypes } from '../../dave-data-module/State/actions/users.actions';
import { ProductEntity } from '../../dave-data-module/entities/product.entity';
import { UserEntity } from '../../dave-data-module/entities/user.entity';
import { State } from '../../dave-data-module/State';
import { AppDialogService } from '../../dave-utils-module/app-dialog-module/app-dialog.service';
import { isNotNullOrUndefined, ProductName } from '../../helper/helper';

const availableIsNumber = (available: 'Unbegrenzt' | number) => available !== 'Unbegrenzt' && isNotNullOrUndefined(available);

@Component({
    selector: 'app-license-list',
    templateUrl: './license-list.component.html',
    styleUrls: ['./license-list.component.scss'],
})
export class LicenseListComponent implements OnInit, OnDestroy {
    @Input() User: UserEntity;
    @Input() EmployeeId: number | null = null;
    @Input() Editing = false;
    @Input() ChangedEmail = false;

    public LicensesForm = new UntypedFormControl(null);
    public Products: ReturnType<typeof getProductsWithMaxUserByPartnerId>;

    public DisplayUser$: Observable<UserEntity & { products: ReturnType<typeof getProductsWithMaxUserByPartnerId> }>;
    public Products$: Observable<ReturnType<typeof getProductsWithMaxUserByPartnerId>>;
    private subscription: Subscription = null;

    constructor(
        private appDialog: AppDialogService,
        private store: Store<State>,
        private cdr: ChangeDetectorRef,
        private route: ActivatedRoute,
    ) {
        const partnerId: number = +this.route.snapshot.paramMap.get('partnerId');
        this.Products$ = this.store.select(
            getProductsWithMaxUserByPartnerId,
            partnerId ? partnerId : this.User ? this.User.PartnerId : null,
        );
    }

    ngOnInit() {
        this.LicensesForm.setValue(this.User.ProductIds);
        this.DisplayUser$ = combineLatest([this.Products$]).pipe(
            map(([products]) => Object.assign(this.User.Clone(), {
                products: this.User && this.User.ProductIds && products
                    ? products.filter(p => {
                        this.User = this.User.Clone({ ProductIds: this.LicensesForm.value });
                        return this.User.ProductIds.find(pId => pId === p.Id);
                    })
                    : [],
            })),
        );
        this.subscription = this.Products$.subscribe(products => {
            this.Products = products;
        });
        this.subscription.add(this.DisplayUser$.subscribe());
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public HasProduct(id: number) {
        return this.LicensesForm.value && this.LicensesForm.value.find((pId: number) => pId === id);
    }

    public SetLicense(license: ProductEntity, email: string, employeeId?: number) {
        if (this.LicensesForm.value && this.LicensesForm.value.find(lId => lId === license.Id)) {
            // ToDo: Remove dirty fix
            if (
                availableIsNumber(
                    this.Products[
                        this.Products.indexOf(this.Products.find(p => p.Id === license.Id))
                        ].Available,
                )
            ) {
                const tmpNumber = this.Products[
                    this.Products.indexOf(this.Products.find(p => p.Id === license.Id))
                    ].Available as number;
                this.Products[
                    this.Products.indexOf(this.Products.find(p => p.Id === license.Id))
                    ].Available = tmpNumber + 1;
            }
            this.LicensesForm.setValue(
                this.LicensesForm.value
                    ? this.LicensesForm.value.find(lId => lId === license.Id)
                    ? this.LicensesForm.value.filter(lId => lId !== license.Id)
                    : [...this.LicensesForm.value, license.Id]
                    : [license.Id],
            );
            this.SaveLicense(employeeId);
        } else {
            this.appDialog
                .OpenConfirmationDialog({
                    paragraph:
                        'Wenn Sie diesem Mitarbeiter eine Lizenz zuordnen, ' +
                        `bekommt er eine E-Mail an die hinterlegte Adresse (${this.User.Email}) ` +
                        'mit einem Aktivierungslink für sein ' + ProductName + '-Konto.',
                    warning: this.ChangedEmail
                        ? 'Sie haben die E-Mail-Adresse geändert, ' +
                        'diese Änderung sollte zuerst gespeichert werden!'
                        : undefined,
                })
                .subscribe(([result,]) => result && this.setLicense(license, employeeId));
        }
    }

    private setLicense(license: ProductEntity, employeeId?: number) {
        // ToDo: Remove dirty fix
        if (
            availableIsNumber(
                this.Products[this.Products.indexOf(this.Products.find(p => p.Id === license.Id))]
                    .Available,
            )
        ) {
            const tmpNumber = this.Products[
                this.Products.indexOf(this.Products.find(p => p.Id === license.Id))
                ].Available as number;
            this.Products[
                this.Products.indexOf(this.Products.find(p => p.Id === license.Id))
                ].Available = tmpNumber - 1;
        }
        this.LicensesForm.setValue(
            this.LicensesForm.value
                ? this.LicensesForm.value.find(lId => lId === license.Id)
                ? this.LicensesForm.value.filter(lId => lId !== license.Id)
                : [...this.LicensesForm.value, license.Id]
                : [license.Id],
        );
        this.cdr.detectChanges();
        this.SaveLicense(employeeId);
    }

    public SaveLicense(employeeId?: number) {
        const userProductIds = this.User.ProductIds.slice().sort();
        const licenseFormValues = this.LicensesForm.value.slice().sort();
        if (
            (this.User &&
                !this.compareArrays(userProductIds, licenseFormValues)) ||
            (!this.User && this.LicensesForm.value && this.LicensesForm.value.length)
        ) {
            if (this.User) {
                this.store.dispatch(
                    UsersActionTypes.ModifyManageUser({
                        Payload: this.User.Clone({ ProductIds: this.LicensesForm.value }),
                    }),
                );
            }
        }
    }

    private compareArrays(array1: any[], array2: any[]) {
        if (!array1 || !array2) {
            return false;
        }

        if (array1.length !== array2.length) {
            return false;
        }

        for (let i = 0, l = array1.length; i < l; i++) {
            if (array1[i] instanceof Array && array2[i] instanceof Array) {
                if (!this.compareArrays(array1[i], array2[i])) {
                    return false;
                }
            } else if (array1[i] !== array2[i]) {
                return false;
            }
        }
        return true;
    }
}
